import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';
import { useAccess } from '../lib/access';
import VendorCard from '../components/VendorCard';
import VendorModal from '../components/VendorModal';

export default function Vendors({ session, currentView, onNavigate }) {
  const canEdit = useAccess().canEditVendors;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('vendors')
      .select('*')
      .order('name');
    if (data) setVendors(data);
    if (error) console.error(error);
    setLoading(false);
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    const { error } = await supabase.from('vendors').delete().eq('id', id);
    if (!error) setVendors(vendors.filter(v => v.id !== id));
  };

  const openEdit = (vendor) => { setEditingVendor(vendor); setShowModal(true); };
  const openAdd = () => { setEditingVendor(null); setShowModal(true); };

  const onSaved = (savedVendor, isNew) => {
    if (isNew) {
      setVendors(prev => [...prev, savedVendor].sort((a, b) => a.name.localeCompare(b.name)));
    } else {
      setVendors(vendors.map(v => v.id === savedVendor.id ? savedVendor : v));
    }
    setShowModal(false);
  };

  // Derive specialties from data
  const specialties = ['All', ...new Set(vendors.map(v => v.specialty).filter(Boolean).sort())];

  // Filter vendors
  const query = searchQuery.toLowerCase();
  const filtered = vendors.filter(v => {
    const specMatch = selectedSpecialty === 'All' || v.specialty === selectedSpecialty;
    const searchMatch = !query ||
      v.name.toLowerCase().includes(query) ||
      (v.specialty && v.specialty.toLowerCase().includes(query)) ||
      (v.email && v.email.toLowerCase().includes(query));
    return specMatch && searchMatch;
  });

  // Stats
  const totalVendors = vendors.length;
  const specialtyCount = new Set(vendors.map(v => v.specialty).filter(Boolean)).size;

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Specialty</div>
          {specialties.map(s => (
            <div
              key={s}
              className={`sidebar-item ${selectedSpecialty === s ? 'active' : ''}`}
              onClick={() => setSelectedSpecialty(s)}
            >
              {s}
              {s !== 'All' && (
                <span className="count">
                  {vendors.filter(v => v.specialty === s).length}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{totalVendors}</span>
            <span className="stat-label">Total Vendors</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{specialtyCount}</span>
            <span className="stat-label">Specialties</span>
          </div>
          <div className="vendor-search">
            <input
              className="search-input"
              type="text"
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          {canEdit && <button className="add-btn" onClick={openAdd}>+ Add Vendor</button>}
        </div>

        {/* Content */}
        <div className="content-area">
          <div className="content-header">
            <h2>Vendor Directory{selectedSpecialty !== 'All' ? ` — ${selectedSpecialty}` : ''}</h2>
            <p>{filtered.length} vendor{filtered.length !== 1 ? 's' : ''}</p>
          </div>

          {loading ? (
            <div className="empty-state"><p>Loading vendors...</p></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <h3>No vendors found</h3>
              <p>{vendors.length === 0 ? 'Add a vendor to get started' : 'Try adjusting your filters'}</p>
            </div>
          ) : (
            <div className="vendor-list">
              {filtered.map(vendor => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onEdit={canEdit ? openEdit : null}
                  onDelete={canEdit ? deleteVendor : null}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <VendorModal
          vendor={editingVendor}
          onSaved={onSaved}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
